// Setup → AI policies → AI rules (api-phase3.md "AI rules"): what happens after the AI decides an event. An ordered
// list, first match wins; global rules, or a site's own that replace them. Supervisors read and try them; admins edit.
//   when   verdict, stage 1 risk and behaviors, confidence below, alarm types, cameras, site-local schedule, stage
//   then   SOC queue at a priority (the AI may not close it), human review of an AI close, email / text to customer
//          contacts or email to staff, a detail report behind an expiring link
//   save   every save is a new version (history below), with a note; a stale editor gets a 409
//   try    which rule each of the last 30 days' events would have matched (reads only)

import { useState } from 'react';
import { api, type AiRule, type AiRulesSetup, type AiRulesTest, type RulePriority, type RuleRecipients } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Empty, ErrorBox, InlineError, Loading, Pill } from '../../components/ui.tsx';
import { useLoad } from '../../hooks.ts';
import { useSession } from '../../session.tsx';
import { fmtDateTime, OPERATOR_TZ } from '../../time.ts';
import { moveItem, SaveBar, Toggle, UpDown } from './common.tsx';

const VERDICTS: [string, string][] = [
  ['clear', 'Clear'],
  ['authorized', 'Authorized'],
  ['monitor', 'Monitor'],
  ['suspicious', 'Suspicious'],
  ['criminal_life_safety', 'Criminal / life safety'],
];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const PRIORITIES: RulePriority[] = ['low', 'normal', 'high', 'critical'];
const label = (s: string) => s.replace(/_/g, ' ');
const csv = (v: string) =>
  v
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

const newRule = (): AiRule => ({ id: crypto.randomUUID(), name: 'New rule', enabled: true, when: {}, then: { queue: { priority: 'normal' }, humanReview: false, notify: [], report: null } });

/** One rule's "when" in a line, for the collapsed card. */
function whenText(r: AiRule): string {
  const w = r.when;
  const p: string[] = [];
  if (w.verdicts?.length) p.push(w.verdicts.map(label).join(' or '));
  if (w.riskAtLeast) p.push(`${w.riskAtLeast}+ risk`);
  if (w.behaviors?.length) p.push(w.behaviors.map(label).join(' / '));
  if (w.confidenceBelow !== undefined) p.push(`confidence < ${Math.round(w.confidenceBelow * 100)}%`);
  if (w.analytics?.length) p.push(`alarm ${w.analytics.join(', ')}`);
  if (w.cameraIds?.length) p.push(`camera ${w.cameraIds.join(', ')}`);
  if (w.schedule) p.push(`${w.schedule.days.map((d) => DAYS[d]).join(' ')} ${w.schedule.from}–${w.schedule.to}`);
  if (w.decidedBy) p.push(w.decidedBy === 'stage1' ? 'decided by stage 1' : 'decided by stage 2');
  return p.length ? p.join(' · ') : 'every event';
}

function thenText(r: AiRule): string {
  const t = r.then;
  const p: string[] = [];
  if (t.queue) p.push(`SOC queue (${t.queue.priority})`);
  if (t.humanReview) p.push('human review');
  for (const n of t.notify) p.push(`${n.channel === 'both' ? 'text + email' : n.channel === 'sms' ? 'text' : 'email'} ${n.to === 'customer' ? 'customer' : 'staff'}`);
  if (t.report) p.push(`report to ${t.report.to === 'customer' ? 'customer' : 'staff'}`);
  return p.join(' · ');
}

function Chips<T extends string>({ all, value, onChange, labelOf, name }: { all: readonly T[] | readonly [T, string][]; value: T[] | undefined; onChange: (v: T[] | undefined) => void; labelOf?: (t: T) => string; name: string }) {
  const items = (all as readonly (T | [T, string])[]).map((x) => (Array.isArray(x) ? x : ([x, labelOf ? labelOf(x) : label(x)] as [T, string])));
  const on = new Set(value ?? []);
  return (
    <div className="chips" role="group" aria-label={name}>
      {items.map(([k, l]) => (
        <button key={k} type="button" className={`chip aichip${on.has(k) ? ' on' : ''}`} aria-pressed={on.has(k)} onClick={() => {
          const n = new Set(on);
          if (n.has(k)) n.delete(k);
          else n.add(k);
          onChange(n.size ? [...n] : undefined);
        }}>
          {l}
        </button>
      ))}
    </div>
  );
}

function StaffPicker({ value, onChange, setup, what }: { value: RuleRecipients; onChange: (v: RuleRecipients) => void; setup: AiRulesSetup; what: string }) {
  return (
    <div className="rulestaff">
      <Chips name={`${what}: staff`} all={setup.staff.map((u) => [u.id, u.name] as [string, string])} value={value.userIds} onChange={(userIds) => onChange({ ...value, userIds })} />
      {setup.groups.length > 0 && <Chips name={`${what}: groups`} all={setup.groups.map((g) => [g.id, `Group: ${g.name}`] as [string, string])} value={value.groupIds} onChange={(groupIds) => onChange({ ...value, groupIds })} />}
    </div>
  );
}

function RuleForm({ r, set, setup, n }: { r: AiRule; set: (r: AiRule) => void; setup: AiRulesSetup; n: number }) {
  const w = r.when;
  const t = r.then;
  const setW = (p: Partial<AiRule['when']>) => set({ ...r, when: Object.fromEntries(Object.entries({ ...w, ...p }).filter(([, v]) => v !== undefined)) as AiRule['when'] });
  const setT = (p: Partial<AiRule['then']>) => set({ ...r, then: { ...t, ...p } });
  const templates = [...new Set(setup.templates.map((x) => x.code))];
  const id = `rule-${n}`;
  return (
    <div className="ruleform">
      <div className="trow">
        <label htmlFor={`${id}-name`}>Name</label>
        <span className="sp" />
        <input id={`${id}-name`} value={r.name} maxLength={80} onChange={(e) => set({ ...r, name: e.target.value })} />
      </div>
      <div className="lbl">When (every condition you set must hold; none set matches every event)</div>
      <div className="trow col">
        <span>AI verdict</span>
        <Chips name="Verdicts" all={VERDICTS.map(([k, l]) => [k, l] as [string, string])} value={w.verdicts} onChange={(verdicts) => setW({ verdicts })} />
      </div>
      <div className="trow col">
        <span>Behaviors stage 1 saw (any of)</span>
        <Chips name="Behaviors" all={setup.behaviors} value={w.behaviors} onChange={(behaviors) => setW({ behaviors })} />
      </div>
      <div className="trow">
        <label htmlFor={`${id}-risk`}>Stage 1 risk at least</label>
        <span className="sp" />
        <select id={`${id}-risk`} className="compact" value={w.riskAtLeast ?? ''} onChange={(e) => setW({ riskAtLeast: (e.target.value || undefined) as AiRule['when']['riskAtLeast'] })}>
          <option value="">Any</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
        </select>
      </div>
      <div className="trow">
        <label htmlFor={`${id}-conf`}>AI confidence below</label>
        <span className="sp" />
        <span className="ctl">
          <input id={`${id}-conf`} type="number" min={1} max={100} step={1} placeholder="any" value={w.confidenceBelow === undefined ? '' : Math.round(w.confidenceBelow * 100)} onChange={(e) => setW({ confidenceBelow: e.target.value ? Number(e.target.value) / 100 : undefined })} />
          <span className="muted">%</span>
        </span>
      </div>
      <div className="trow">
        <label htmlFor={`${id}-an`}>Alarm types (comma-separated, any of)</label>
        <span className="sp" />
        <input id={`${id}-an`} placeholder="e.g. person, line_crossing" defaultValue={(w.analytics ?? []).join(', ')} onBlur={(e) => setW({ analytics: csv(e.target.value).length ? csv(e.target.value) : undefined })} />
      </div>
      <div className="trow">
        <label htmlFor={`${id}-cam`}>Camera IDs (comma-separated, any of)</label>
        <span className="sp" />
        <input id={`${id}-cam`} className="mono" placeholder="any camera" defaultValue={(w.cameraIds ?? []).join(', ')} onBlur={(e) => setW({ cameraIds: csv(e.target.value).length ? csv(e.target.value) : undefined })} />
      </div>
      <div className="trow">
        <Toggle label="Only during a schedule (site time)" checked={!!w.schedule} onChange={(v) => setW({ schedule: v ? { days: [0, 1, 2, 3, 4, 5, 6], from: '22:00', to: '06:00' } : undefined })} />
        <span className="sp" />
        {w.schedule && (
          <span className="ctl">
            <input type="time" aria-label="From" value={w.schedule.from} onChange={(e) => setW({ schedule: { ...w.schedule!, from: e.target.value } })} />
            <span className="muted">to</span>
            <input type="time" aria-label="To" value={w.schedule.to} onChange={(e) => setW({ schedule: { ...w.schedule!, to: e.target.value } })} />
          </span>
        )}
      </div>
      {w.schedule && <Chips name="Days" all={DAYS.map((d, i) => [String(i), d] as [string, string])} value={w.schedule.days.map(String)} onChange={(v) => setW({ schedule: { ...w.schedule!, days: (v ?? []).map(Number).sort() } })} />}
      <div className="trow">
        <label htmlFor={`${id}-by`}>Decided by</label>
        <span className="sp" />
        <select id={`${id}-by`} className="compact" value={w.decidedBy ?? ''} onChange={(e) => setW({ decidedBy: (e.target.value || undefined) as AiRule['when']['decidedBy'] })}>
          <option value="">Either stage</option>
          <option value="stage1">Stage 1 (behavior assessment)</option>
          <option value="stage2">Stage 2 (contextual review)</option>
        </select>
      </div>

      <div className="lbl">Then</div>
      <div className="trow">
        <Toggle label="Send to the SOC queue (the AI may not close it)" checked={!!t.queue} onChange={(v) => setT({ queue: v ? { priority: 'normal' } : null })} />
        <span className="sp" />
        {t.queue && (
          <select className="compact" aria-label="Queue priority" value={t.queue.priority} onChange={(e) => setT({ queue: { priority: e.target.value as RulePriority } })}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p} priority or higher
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="trow">
        <Toggle label="Human review: a supervisor reviews it if the AI closes it" checked={t.humanReview} onChange={(v) => setT({ humanReview: v })} />
      </div>
      {t.notify.map((x, j) => (
        <div key={j} className="rulenotify">
          <div className="trow">
            <span>Notify</span>
            <span className="sp" />
            <select className="compact" aria-label="Who" value={x.to} onChange={(e) => setT({ notify: t.notify.map((y, k) => (k === j ? (e.target.value === 'staff' ? { to: 'staff', channel: 'email', template: y.template } : { to: 'customer', channel: y.channel, template: y.template }) : y)) })}>
              <option value="customer">Site's customer contacts</option>
              <option value="staff">EyeQ staff</option>
            </select>
            <select className="compact" aria-label="How" value={x.channel} disabled={x.to === 'staff'} onChange={(e) => setT({ notify: t.notify.map((y, k) => (k === j ? { ...y, channel: e.target.value as 'email' | 'sms' | 'both' } : y)) })}>
              <option value="sms">Text</option>
              <option value="email">Email</option>
              <option value="both">Text and email</option>
            </select>
            <select className="compact" aria-label="Template" value={x.template} onChange={(e) => setT({ notify: t.notify.map((y, k) => (k === j ? { ...y, template: e.target.value } : y)) })}>
              {templates.map((c) => (
                <option key={c} value={c}>
                  {setup.templates.find((tp) => tp.code === c)?.name ?? c}
                </option>
              ))}
            </select>
            <button type="button" className="btn icon sm" aria-label="Remove notification" onClick={() => setT({ notify: t.notify.filter((_, k) => k !== j) })}>
              <Icon name="x" />
            </button>
          </div>
          {x.to === 'staff' && <StaffPicker setup={setup} what="Notify" value={x} onChange={(v) => setT({ notify: t.notify.map((y, k) => (k === j ? { ...y, ...v } : y)) })} />}
          {x.to === 'customer' && <div className="note">Goes to the site's notification recipients (Setup → site → Notifications) on the chosen channel.</div>}
        </div>
      ))}
      {t.notify.length < 5 && (
        <button type="button" className="btn sm" onClick={() => setT({ notify: [...t.notify, { to: 'customer', channel: 'sms', template: templates[0] ?? 'activity' }] })}>
          <Icon name="plus" />
          Add a notification
        </button>
      )}
      <div className="trow">
        <Toggle label="Send a detail report (an expiring link to a read-only page)" checked={!!t.report} onChange={(v) => setT({ report: v ? { to: 'customer', expiresHours: 72 } : null })} />
        <span className="sp" />
        {t.report && (
          <span className="ctl">
            <select className="compact" aria-label="Report to" value={t.report.to} onChange={(e) => setT({ report: { to: e.target.value as 'customer' | 'staff', expiresHours: t.report!.expiresHours } })}>
              <option value="customer">Site's report recipients</option>
              <option value="staff">EyeQ staff</option>
            </select>
            <input type="number" aria-label="Link works for hours" min={1} max={720} value={t.report.expiresHours} onChange={(e) => setT({ report: { ...t.report!, expiresHours: Number(e.target.value) } })} />
            <span className="muted">hours</span>
          </span>
        )}
      </div>
      {t.report?.to === 'staff' && <StaffPicker setup={setup} what="Report" value={t.report} onChange={(v) => setT({ report: { ...t.report!, ...v } })} />}
    </div>
  );
}

export function AiRulesEditor({ siteId }: { siteId: string | null }) {
  const s = useSession();
  const canEdit = s.isAdmin;
  const load = useLoad((sig) => api.aiRules(siteId, sig), [siteId]);
  const [draft, setDraft] = useState<AiRule[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const [test, setTest] = useState<AiRulesTest | null>(null);
  const d = load.data;
  if (load.error && !d) return <ErrorBox error={load.error} onRetry={load.reload} title="Could not load the AI rules" />;
  if (!d) return <Loading label="Loading AI rules…" />;
  const rules = draft ?? d.ruleSet.rules;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(d.ruleSet.rules);
  const edit = (next: AiRule[]) => {
    setSaved(false);
    setTest(null);
    setDraft(next);
  };
  const run = async (f: () => Promise<Pick<AiRulesSetup, 'ruleSet' | 'history'>>) => {
    setBusy(true);
    setError(null);
    try {
      const r = await f();
      load.setData((o) => (o ? { ...o, ...r } : o));
      setDraft(null);
      setNote('');
      setSaved(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const tryRules = async () => {
    setError(null);
    try {
      setTest(await api.testAiRules(siteId, rules));
    } catch (e) {
      setError(e);
    }
  };
  const count = (id: string) => test?.byRule.find((b) => b.ruleId === id)?.matched;
  const scope = d.ruleSet.scope;
  return (
    <section className="card2">
      <div className="card-h">
        <h3>
          <Icon name="list" />
          AI rules
        </h3>
        <span className="card-r row-wrap">
          {scope === 'site' ? <Pill tone="ai">This site's rules</Pill> : scope === 'inherited' ? <Pill>Uses the global rules</Pill> : <Pill>Global rules</Pill>}
          <span className="note">version {d.ruleSet.version}</span>
        </span>
      </div>
      <div className="note">
        After the AI decides an event, the first rule that matches acts; later rules are skipped. Rules only add caution: autonomy levels and guardrails still apply. At Level 0 a match is recorded, and nothing is sent.
        {scope === 'inherited' && ' Saving here gives this site its own rules in place of the global ones.'}
      </div>
      <fieldset disabled={!canEdit || busy} className="rulelist">
        {rules.length === 0 && <Empty>No rules: the AI's decision stands, with nothing extra.</Empty>}
        {rules.map((r, i) => (
          <div key={r.id} className={`rulecard${r.enabled ? '' : ' off'}`}>
            <div className="rulehead">
              <span className="mono dim">{i + 1}</span>
              <button type="button" className="rulename" onClick={() => setOpen(open === r.id ? null : r.id)} aria-expanded={open === r.id}>
                <b>{r.name}</b>
                <small>
                  When {whenText(r)} → {thenText(r) || 'nothing'}
                </small>
              </button>
              {count(r.id) !== undefined && <Pill tone="ai" title={`Would have matched in the last ${test!.days} days`}>{count(r.id)} events</Pill>}
              <Toggle label="On" checked={r.enabled} onChange={(v) => edit(rules.map((x) => (x.id === r.id ? { ...x, enabled: v } : x)))} />
              <UpDown i={i} n={rules.length} what={r.name} onMove={(dl) => edit(moveItem(rules, i, dl))} />
              <button type="button" className="btn icon sm" aria-label={`Delete ${r.name}`} onClick={() => edit(rules.filter((x) => x.id !== r.id))}>
                <Icon name="trash" />
              </button>
            </div>
            {open === r.id && <RuleForm r={r} n={i} setup={d} set={(n) => edit(rules.map((x) => (x.id === r.id ? n : x)))} />}
          </div>
        ))}
        {canEdit && rules.length < 50 && (
          <button
            type="button"
            className="btn sm"
            onClick={() => {
              const r = newRule();
              edit([...rules, r]);
              setOpen(r.id);
            }}
          >
            <Icon name="plus" />
            Add a rule
          </button>
        )}
      </fieldset>
      <div className="row-wrap">
        <button type="button" className="btn sm" onClick={() => void tryRules()} disabled={busy || !rules.length}>
          <Icon name="spark" />
          Try on the last 30 days
        </button>
        {test && (
          <span className="note">
            {test.events} events with an AI verdict · {test.events - test.noMatch} matched a rule · {test.noMatch} matched none
          </span>
        )}
        <span className="sp" />
        {canEdit && scope === 'site' && siteId && (
          <button type="button" className="btn sm" disabled={busy} onClick={() => void run(() => api.inheritAiRules(siteId, d.ruleSet.editVersion))}>
            <Icon name="sync" />
            Use the global rules
          </button>
        )}
      </div>
      {canEdit && dirty && <input aria-label="What changed" placeholder="What changed and why (kept with this version)" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />}
      <InlineError error={error} />
      <SaveBar
        canEdit={canEdit}
        dirty={dirty}
        busy={busy}
        error={null}
        saved={saved}
        label="Save rules"
        onSave={() => void run(() => api.putAiRules(siteId, rules, d.ruleSet.editVersion, note))}
        onReset={() => {
          setDraft(null);
          setTest(null);
          setError(null);
        }}
      />
      {d.history.length > 0 && (
        <details>
          <summary className="note">History ({d.history.length})</summary>
          <table className="tbl">
            <tbody>
              {d.history.map((h) => (
                <tr key={h.id}>
                  <td className="mono">v{h.version}</td>
                  <td>{fmtDateTime(h.createdAt, OPERATOR_TZ)}</td>
                  <td>{h.inherit ? 'Back to the global rules' : `${h.rules} rule${h.rules === 1 ? '' : 's'}`}</td>
                  <td className="dim">{h.note ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
}
